import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useTenant } from '../context/TenantContext';
import { useLabels } from '../utils/labels';
import {
  Box,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Grid,
  Autocomplete,
  Chip,
  Divider,
  FormControlLabel,
  Checkbox,
  Paper,
  Drawer,
  IconButton
} from '@mui/material';
import { DatePicker, TimePicker } from '@mui/x-date-pickers';
import dayjs from 'dayjs';
import { MdClose } from 'react-icons/md';
import { postData } from '../utils/serverHelper';
import { GOOGLE_MEET_GENERATE } from '../utils/urls';
import { toast } from 'react-toastify';
import MoreFields from './MoreFields';

export const AppointmentForm = ({ 
  open = false,
  onClose,
  data = null, 
  onSubmit, 
  mode = 'add' // 'add' or 'edit'
}) => {
  const selector = useSelector((state) => state.business);
  const selectorAuth = useSelector((state) => state.auth);
  const { tenant } = useTenant();
  const labels = useLabels();
  
  const [values, setValues] = useState({
    patient_id: data?.patient_id || '',
    service_id: data?.service_id || '',
    staff_id: data?.staff_id || '',
    start_date: data?.start_date ? dayjs(data.start_date) : dayjs(),
    start_time: data?.start_time ? dayjs(`${data.start_date}T${data.start_time}`) : dayjs().add(1, 'hour'),
    end_date: data?.end_date ? dayjs(data.end_date) : dayjs(),
    end_time: data?.end_time ? dayjs(`${data.end_date}T${data.end_time}`) : dayjs().add(1, 'hour').add(30, 'minute'),
    reason: data?.reason || '',
    notes: data?.notes || '',
    appointment_type: data?.appointment_type || 'in_person',
    location: data?.location || '',
    payment_method: data?.payment_method || '',
    payment_status: data?.payment_status || 'pending',
    priority: data?.priority || 'normal',
    send_reminder: data?.send_reminder !== undefined ? data.send_reminder : true,
    duration: data?.duration || 30,
    meet_link: data?.meet_link || '',
  });

  const [errors, setErrors] = useState({});
  const [customFields, setCustomFields] = useState({});
  useEffect(() => {
    if (open) setCustomFields({});
  }, [open, data?.appointment_id]);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [selectedService, setSelectedService] = useState(null);
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [generatingMeetLink, setGeneratingMeetLink] = useState(false);

  useEffect(() => {
    if (data) {
      // Find selected patient, service, and staff
      const patient = selector?.patients?.find(p => p.patient_id === data.patient_id);
      const service = selector?.services?.find(s => s.service_id === data.service_id);
      const staff = selector?.staff?.find(s => s.staff_id === data.staff_id);
      
      setSelectedPatient(patient || null);
      setSelectedService(service || null);
      setSelectedStaff(staff || null);
    }
  }, [data, selector]);

  useEffect(() => {
    // Auto-calculate end time based on start time and duration
    if (values.start_time && values.duration) {
      const endTime = values.start_time.add(values.duration, 'minute');
      setValues(prev => ({
        ...prev,
        end_time: endTime,
        end_date: values.start_date
      }));
    }
  }, [values.start_time, values.duration, values.start_date]);

  const handleChange = (e) => {
    const { name, value, checked, type } = e.target;
    setValues(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handlePatientChange = (event, newValue) => {
    setSelectedPatient(newValue);
    setValues(prev => ({
      ...prev,
      patient_id: newValue?.patient_id || ''
    }));
  };

  const handleServiceChange = (event, newValue) => {
    setSelectedService(newValue);
    setValues(prev => ({
      ...prev,
      service_id: newValue?.service_id || ''
    }));
    
    // Auto-set duration from service if available
    if (newValue?.duration) {
      setValues(prev => ({
        ...prev,
        duration: newValue.duration
      }));
    }
  };

  const handleStaffChange = (event, newValue) => {
    setSelectedStaff(newValue);
    setValues(prev => ({
      ...prev,
      staff_id: newValue?.staff_id || ''
    }));
  };

  const validate = () => {
    const newErrors = {};
    if (!values.patient_id) newErrors.patient_id = `${labels.customer || "Customer"} is required`;
    if (!values.service_id) newErrors.service_id = 'Service is required';
    if (!values.staff_id) newErrors.staff_id = 'Staff is required';
    if (!values.start_date) newErrors.start_date = 'Start date is required';
    if (!values.start_time) newErrors.start_time = 'Start time is required';
    if (!values.duration || values.duration < 15) newErrors.duration = 'Duration must be at least 15 minutes';
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validate()) return;

    const submitData = {
      patient_id: values.patient_id,
      service_id: values.service_id.toString(),
      customer_id: values.patient_id,
      staff_id: values.staff_id,
      start_date: values.start_date.format('YYYY-MM-DD'),
      start_time: values.start_time.format('HH:mm:ss'),
      end_date: values.end_date.format('YYYY-MM-DD'),
      end_time: values.end_time.format('HH:mm:ss'),
      reason: values.reason,
      notes: values.notes,
      // meet_link: values.meet_link || null,
      appointment_type: values.appointment_type,
      location: values.location,
      payment_method: values.payment_method,
      payment_status: values.payment_status,
      priority: values.priority,
      send_reminder: values.send_reminder,
      duration: values.duration,
      meet_link: values.meet_link || "",
      custom_fields: customFields,
    };

    onSubmit(submitData);
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: "100%", sm: "90%", md: "600px", lg: "700px" },
          maxWidth: "700px",
        },
      }}
    >
      <Box component="form" onSubmit={handleSubmit} sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
        {/* Header */}
        <Box
          sx={{
            p: 3,
            borderBottom: "1px solid #e0e0e0",
            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            color: "white",
          }}
        >
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Typography variant="h5" sx={{ fontWeight: "bold" }}>
              {(!data || mode === "add") ? "Add New Appointment" : "Edit Appointment"}
            </Typography>
            <IconButton onClick={onClose} sx={{ color: "white" }}>
              <MdClose />
            </IconButton>
          </Box>
        </Box>

        {/* Content */}
        <Box sx={{ flex: 1, overflow: "auto", p: 3 }}>
          <Grid container spacing={2}>
        {/* Customer/Patient Selection */}
        <Grid item xs={12}>
          <Autocomplete
            options={selector?.patients || []}
            getOptionLabel={(option) => option.full_name ? `${option.full_name} (${option.contact || 'No phone'})` : ''}
            value={selectedPatient}
            onChange={handlePatientChange}
            filterOptions={(options, { inputValue }) => {
              if (!inputValue) return options;
              const search = inputValue.toLowerCase();
              return options.filter((option) => {
                return (
                  option.full_name?.toLowerCase().includes(search) ||
                  option.contact?.includes(search) ||
                  option.email?.toLowerCase().includes(search)
                );
              });
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label={`${labels.customer || "Customer"} *`}
                error={!!errors.patient_id}
                helperText={errors.patient_id || `Select a ${labels.customer?.toLowerCase() || "customer"} from the list`}
                placeholder={`Search and select ${labels.customer?.toLowerCase() || "customer"}...`}
                variant="outlined"
                size="small"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '&:hover fieldset': {
                      borderColor: '#667eea',
                    },
                  },
                }}
              />
            )}
          />
        </Grid>

        {/* Service Selection */}
        <Grid item xs={12} sm={6}>
          <Autocomplete
            options={selector?.services || []}
            getOptionLabel={(option) => option.service || ''}
            value={selectedService}
            onChange={handleServiceChange}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Service *"
                error={!!errors.service_id}
                helperText={errors.service_id || "Select a service"}
                placeholder="Search and select service..."
                variant="outlined"
                size="small"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '&:hover fieldset': {
                      borderColor: '#667eea',
                    },
                  },
                }}
              />
            )}
          />
        </Grid>

        {/* Staff Selection */}
        <Grid item xs={12} sm={6}>
          <Autocomplete
            options={selector?.staff || []}
            getOptionLabel={(option) => option.name || ''}
            value={selectedStaff}
            onChange={handleStaffChange}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Staff *"
                error={!!errors.staff_id}
                helperText={errors.staff_id || "Select assigned staff member"}
                placeholder="Search and select staff..."
                variant="outlined"
                size="small"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '&:hover fieldset': {
                      borderColor: '#667eea',
                    },
                  },
                }}
              />
            )}
          />
        </Grid>

        {/* Date and Time */}
        <Grid item xs={12} sm={6}>
          <DatePicker
            label="Start Date *"
            value={values.start_date}
            onChange={(newValue) => setValues(prev => ({ ...prev, start_date: newValue, end_date: newValue }))}
            slotProps={{
              textField: {
                fullWidth: true,
                size: 'small',
                error: !!errors.start_date,
                helperText: errors.start_date || "Select appointment date",
                variant: 'outlined',
                sx: {
                  '& .MuiOutlinedInput-root': {
                    '&:hover fieldset': {
                      borderColor: '#667eea',
                    },
                  },
                }
              }
            }}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <TimePicker
            label="Start Time *"
            value={values.start_time}
            onChange={(newValue) => setValues(prev => ({ ...prev, start_time: newValue }))}
            slotProps={{
              textField: {
                fullWidth: true,
                size: 'small',
                error: !!errors.start_time,
                helperText: errors.start_time || "Select appointment start time",
                variant: 'outlined',
                sx: {
                  '& .MuiOutlinedInput-root': {
                    '&:hover fieldset': {
                      borderColor: '#667eea',
                    },
                  },
                }
              }
            }}
          />
        </Grid>

        {/* Duration */}
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth size="small" error={!!errors.duration}>
            <InputLabel>Duration (minutes) *</InputLabel>
            <Select
              name="duration"
              value={values.duration}
              onChange={handleChange}
              label="Duration (minutes) *"
            >
              <MenuItem value={15}>15 minutes</MenuItem>
              <MenuItem value={30}>30 minutes</MenuItem>
              <MenuItem value={45}>45 minutes</MenuItem>
              <MenuItem value={60}>60 minutes</MenuItem>
              <MenuItem value={90}>90 minutes</MenuItem>
              <MenuItem value={120}>120 minutes</MenuItem>
            </Select>
            {errors.duration && (
              <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                {errors.duration}
              </Typography>
            )}
          </FormControl>
        </Grid>

        {/* Appointment Type */}
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth size="small">
            <InputLabel>Appointment Type</InputLabel>
            <Select
              name="appointment_type"
              value={values.appointment_type}
              onChange={handleChange}
              label="Appointment Type"
            >
              <MenuItem value="in_person">In Person</MenuItem>
              <MenuItem value="virtual">Virtual/Online</MenuItem>
              <MenuItem value="phone">Phone Consultation</MenuItem>
              <MenuItem value="home_visit">Home Visit</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        {/* Priority */}
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth size="small">
            <InputLabel>Priority</InputLabel>
            <Select
              name="priority"
              value={values.priority}
              onChange={handleChange}
              label="Priority"
            >
              <MenuItem value="low">Low</MenuItem>
              <MenuItem value="normal">Normal</MenuItem>
              <MenuItem value="high">High</MenuItem>
              <MenuItem value="urgent">Urgent</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        {/* Payment Method */}
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth size="small">
            <InputLabel>Payment Method</InputLabel>
            <Select
              name="payment_method"
              value={values.payment_method}
              onChange={handleChange}
              label="Payment Method"
            >
              <MenuItem value="">Not Selected</MenuItem>
              <MenuItem value="cash">Cash</MenuItem>
              <MenuItem value="card">Card</MenuItem>
              <MenuItem value="upi">UPI</MenuItem>
              <MenuItem value="online">Online</MenuItem>
              <MenuItem value="insurance">Insurance</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        {/* Payment Status */}
        {mode === 'edit' && (
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth size="small">
              <InputLabel>Payment Status</InputLabel>
              <Select
                name="payment_status"
                value={values.payment_status}
                onChange={handleChange}
                label="Payment Status"
              >
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="partial">Partial</MenuItem>
                <MenuItem value="paid">Paid</MenuItem>
                <MenuItem value="refunded">Refunded</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        )}

        {/* Location */}
        <Grid item xs={12}>
          <TextField
            fullWidth
            name="location"
            label="Location/Venue"
            value={values.location}
            onChange={handleChange}
            variant="outlined"
            size="small"
            placeholder="e.g., Room 101, Main Clinic"
            helperText="Physical location for in-person appointments (optional)"
            sx={{
              '& .MuiOutlinedInput-root': {
                '&:hover fieldset': {
                  borderColor: '#667eea',
                },
              },
            }}
          />
        </Grid>

        {/* Google Meet Link - Show only for virtual appointments */}
        {values.appointment_type === 'virtual' && (
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
              <TextField
                fullWidth
                name="meet_link"
                label="Google Meet Link"
                value={values.meet_link}
                onChange={handleChange}
                variant="outlined"
                size="small"
                placeholder="Click Generate to create a Meet link"
                InputProps={{
                  readOnly: true,
                }}
                helperText={values.meet_link ? "Meet link generated successfully" : "Generate a Google Meet link for this virtual appointment"}
              />
              <Button
                variant="outlined"
                onClick={async () => {
                  if (!values.start_date || !values.start_time || !values.end_date || !values.end_time) {
                    toast.error('Please select appointment date and time first');
                    return;
                  }
                  setGeneratingMeetLink(true);
                  try {
                    const response = await postData(GOOGLE_MEET_GENERATE, {
                      start_date: values.start_date.format('YYYY-MM-DD'),
                      start_time: values.start_time.format('HH:mm:ss'),
                      end_date: values.end_date.format('YYYY-MM-DD'),
                      end_time: values.end_time.format('HH:mm:ss'),
                      patient_name: selectedPatient?.full_name || '',
                      patient_email: selectedPatient?.email || '',
                      reason: values.reason || '',
                    });
                    
                    if (response?.success && response?.data?.meetLink) {
                      setValues(prev => ({ ...prev, meet_link: response.data.meetLink }));
                      toast.success('Google Meet link generated successfully!');
                    } else if (response?.meetLink) {
                      setValues(prev => ({ ...prev, meet_link: response.meetLink }));
                      toast.success('Google Meet link generated successfully!');
                    } else {
                      toast.error('Failed to generate Meet link');
                    }
                  } catch (error) {
                    console.error('Error generating Meet link:', error);
                    toast.error(error?.response?.data?.error?.message || 'Failed to generate Meet link');
                  } finally {
                    setGeneratingMeetLink(false);
                  }
                }}
                disabled={generatingMeetLink || !values.start_date || !values.start_time}
                sx={{
                  textTransform: 'none',
                  minWidth: 120,
                  whiteSpace: 'nowrap',
                }}
              >
                {generatingMeetLink ? 'Generating...' : 'Generate Link'}
              </Button>
            </Box>
            {values.meet_link && (
              <Box sx={{ mt: 1 }}>
                <Button
                  variant="text"
                  size="small"
                  href={values.meet_link}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    textTransform: 'none',
                    color: '#5c6ac4',
                    textDecoration: 'underline',
                  }}
                >
                  Open Meet Link
                </Button>
              </Box>
            )}
          </Grid>
        )}

        {/* Reason */}
        <Grid item xs={12}>
          <TextField
            fullWidth
            name="reason"
            label="Reason for Appointment"
            value={values.reason}
            onChange={handleChange}
            variant="outlined"
            size="small"
            multiline
            rows={2}
            placeholder="e.g., Regular checkup, Follow-up consultation"
            helperText="Brief description of appointment purpose"
            sx={{
              '& .MuiOutlinedInput-root': {
                '&:hover fieldset': {
                  borderColor: '#667eea',
                },
              },
            }}
          />
        </Grid>

        {/* Notes */}
        <Grid item xs={12}>
          <TextField
            fullWidth
            name="notes"
            label="Additional Notes"
            value={values.notes}
            onChange={handleChange}
            variant="outlined"
            size="small"
            multiline
            rows={3}
            placeholder={`e.g., ${labels.customer || "Customer"} prefers morning appointments, has mobility issues`}
            helperText="Any additional information or special instructions"
            sx={{
              '& .MuiOutlinedInput-root': {
                '&:hover fieldset': {
                  borderColor: '#667eea',
                },
              },
            }}
          />
        </Grid>

        {/* Send Reminder */}
        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Checkbox
                name="send_reminder"
                checked={values.send_reminder}
                onChange={handleChange}
              />
            }
            label={`Send reminder notification to ${labels.customer?.toLowerCase() || "customer"}`}
          />
        </Grid>
          </Grid>
          <MoreFields
            module="appointments"
            recordId={mode === 'edit' ? (data?.appointment_id || data?.id) : null}
            values={customFields}
            onChange={setCustomFields}
          />
        </Box>

        {/* Footer */}
        <Box
          sx={{
            p: 3,
            borderTop: "1px solid #e0e0e0",
            display: "flex",
            gap: 2,
            justifyContent: "flex-end",
          }}
        >
          <Button
            variant="outlined"
            onClick={onClose}
            sx={{
              textTransform: 'none',
              px: 3,
              borderColor: '#d1d5db',
              color: '#374151',
              '&:hover': {
                borderColor: '#9ca3af',
                backgroundColor: '#f9fafb',
              },
            }}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            sx={{
              textTransform: 'none',
              px: 3,
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              '&:hover': {
                background: "linear-gradient(135deg, #5568d3 0%, #6a3d8f 100%)",
              },
            }}
          >
            {mode === 'edit' ? 'Update Appointment' : 'Create Appointment'}
          </Button>
        </Box>
      </Box>
    </Drawer>
  );
};

